import {
  descendantsOf,
  isDescendant,
  isRootId,
  positionsBetween,
  rootKeyOf,
  type BookmarkChange,
  type BookmarkRow,
} from '@bsync/shared';
import type { SyncCore } from './core';
import { randomId } from './ids';

export class EditError extends Error {}

/**
 * High-level edits for clients that manipulate the replica directly (the web
 * dashboard). Every method stages changes in the outbox, so edits apply
 * optimistically and survive going offline.
 */
export class BookmarkEditor {
  constructor(private readonly core: SyncCore) {}

  private row(id: string): BookmarkRow {
    const r = this.core.tree().rows.get(id);
    if (!r || r.deleted) throw new EditError('This item no longer exists.');
    return r;
  }

  private assertContainer(parentId: string) {
    if (isRootId(parentId)) {
      if (!rootKeyOf(parentId)) throw new EditError('Unknown root folder.');
      return;
    }
    if (this.row(parentId).kind !== 'folder') throw new EditError('Items can only be placed in folders.');
  }

  /** `count` fresh positions at `index` among `parentId`'s children (ignoring `exclude`). */
  positionsAt(parentId: string, index: number | undefined, count: number, exclude: ReadonlySet<string> = new Set()): string[] {
    const tree = this.core.tree();
    const siblings = (tree.childrenOf.get(parentId) ?? []).filter((id) => !exclude.has(id));
    const i = Math.max(0, Math.min(index ?? siblings.length, siblings.length));
    const left = i > 0 ? tree.rows.get(siblings[i - 1]!)!.position : null;
    const right = i < siblings.length ? tree.rows.get(siblings[i]!)!.position : null;
    return positionsBetween(left, right, count);
  }

  async createFolder(parentId: string, title: string, index?: number): Promise<string> {
    return this.create(parentId, 'folder', title.trim() || 'New folder', null, index);
  }

  async createBookmark(parentId: string, title: string, url: string, index?: number): Promise<string> {
    const clean = normalizeInputUrl(url);
    return this.create(parentId, 'bookmark', title.trim() || clean, clean, index);
  }

  private async create(parentId: string, kind: 'folder' | 'bookmark', title: string, url: string | null, index?: number) {
    this.assertContainer(parentId);
    const id = randomId();
    const [position] = this.positionsAt(parentId, index, 1);
    await this.core.stage([
      {
        id,
        kind,
        content: { title, url, hlc: this.core.tick() },
        location: { parentId, position: position!, hlc: this.core.tick() },
      },
    ]);
    return id;
  }

  async update(id: string, patch: { title?: string; url?: string }): Promise<void> {
    const r = this.row(id);
    const title = patch.title !== undefined ? patch.title.trim() : r.title;
    const url = r.kind === 'bookmark' ? (patch.url !== undefined ? normalizeInputUrl(patch.url) : r.url) : null;
    if (title === r.title && url === r.url) return;
    await this.core.stage([{ id, content: { title, url, hlc: this.core.tick() } }]);
  }

  /** Move `ids` (in the given order) to `index` within `parentId`. */
  async move(ids: string[], parentId: string, index?: number): Promise<void> {
    if (!ids.length) return;
    this.assertContainer(parentId);
    const tree = this.core.tree();
    for (const id of ids) {
      if (isRootId(id)) throw new EditError('Root folders cannot be moved.');
      this.row(id);
      if (id === parentId || (!isRootId(parentId) && isDescendant(tree, parentId, id))) {
        throw new EditError('A folder cannot be moved into itself.');
      }
    }
    const moving = new Set(ids);
    // `index` refers to the list the user sees, which still contains the moved
    // items: translate it to an index among the remaining siblings.
    const siblings = tree.childrenOf.get(parentId) ?? [];
    const target = index === undefined ? undefined : siblings.slice(0, index).filter((id) => !moving.has(id)).length;
    const positions = this.positionsAt(parentId, target, ids.length, moving);
    await this.core.stage(ids.map((id, i) => ({ id, location: { parentId, position: positions[i]!, hlc: this.core.tick() } })));
  }

  /** Delete nodes and everything inside them. Returns every id deleted (for undo). */
  async remove(ids: string[]): Promise<string[]> {
    const tree = this.core.tree();
    const all = new Set<string>();
    for (const id of ids) {
      if (isRootId(id)) throw new EditError('Root folders cannot be deleted.');
      if (!tree.parentOf.has(id)) continue;
      all.add(id);
      for (const d of descendantsOf(tree, id)) all.add(d);
    }
    const changes: BookmarkChange[] = [...all].map((id) => ({
      id,
      kind: tree.rows.get(id)!.kind,
      deletion: { deleted: true, hlc: this.core.tick() },
    }));
    await this.core.stage(changes);
    return [...all];
  }

  /** Undo a deletion: clear the tombstones of `ids` (their last location and content are kept). */
  async restore(ids: string[]): Promise<void> {
    const rows = this.core.optimisticRows();
    const changes: BookmarkChange[] = [];
    for (const id of ids) {
      const r = rows.get(id);
      if (r?.deleted) changes.push({ id, kind: r.kind, deletion: { deleted: false, hlc: this.core.tick() } });
    }
    await this.core.stage(changes);
  }
}

export function normalizeInputUrl(raw: string): string {
  const v = raw.trim();
  if (!v) throw new EditError('A URL is required.');
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return v;
  return `https://${v}`;
}
